/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IHelp, IPaymentMethods } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-sales-line';
import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/./order-confirmation';
import { IOrderConfirmationProps } from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import { AttributeValueBase, SalesOrder, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ICultureInfoFormatter } from '@msdyn365-commerce/core-internal';
import { OrderConfirmationDetail } from './components/OrderConfirmationDetail';
import { InvoicePaymentConfirmationDetail } from './components/InvoicePaymentConfirmationDetail';
import { IOrderConfirmationData } from '@msdyn365-commerce-modules/order-management';

interface IPaymentMethodsExt extends IPaymentMethods {
    totalOnAccount?: string;
}

interface ExtendedAttributeValueBase extends AttributeValueBase {
    TextValue?: string;
}

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationPayment: React.FC<IPaymentMethodsExt> = ({ paymentMethodsProps, title, methods, totalOnAccount }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {/* {totalOnAccount ? <p>On Account: {totalOnAccount}</p> : null} */}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

export interface IChargeLinesBreakdownData {
    chargeLineLabel?: string;
    chargeCode?: string;
}

export const renderLineChargeNode = (chargeName: string, chargeAmount: string): React.ReactNode => {
    return (
        <div className={'order-confirmation_order-total'}>
            <p className='order-confirmation_other-charges'>
                <span className='order-information__copy--bold'>{chargeName}: </span>
                <span className='order-information__copy--regular'>{chargeAmount}</span>
            </p>
        </div>
    );
};

export const otherChargesLines = (
    salesOrder: SalesOrder,
    chargeLinesBreakdown: IChargeLinesBreakdownData[],
    cultureFormatter: ICultureInfoFormatter
) => {
    const chargeCodes = chargeLinesBreakdown;
    let lines = salesOrder.ChargeLines;
    let linesNode: React.ReactNode[] = [];
    let otherChargeTotal = salesOrder.ChargeAmount || 0;
    let currencyFormatter = cultureFormatter.formatCurrency;

    chargeCodes?.map(c => {
        const node = lines
            ?.filter(line => line.ChargeCode === c.chargeCode)
            .map(r => {
                otherChargeTotal -= r.CalculatedAmount || 0;
                return renderLineChargeNode(c.chargeLineLabel || '', currencyFormatter(r.CalculatedAmount?.toString() ?? ''));
            });
        linesNode.push(node);
    });

    if (otherChargeTotal >= 0.01) {
        const otherChargesNode = renderLineChargeNode('Other Charges', currencyFormatter(otherChargeTotal.toString() ?? ''));
        linesNode.push(otherChargesNode);
    }

    return <>{linesNode}</>;
};

const getPaymentMethod = (salesOrder?: SalesOrder): string => {
    const paymentAttr = (salesOrder?.AttributeValues as ExtendedAttributeValueBase[])?.find(attr => attr.Name === 'PaymentType');
    return `Payment made via ${paymentAttr?.TextValue?.trim() || 'Credit'} Card`;
};

const renderInvoicePaymentMethod = (salesOrder?: SalesOrder): JSX.Element => (
    <div className='ms-order-confirmation__payment-methods'>
        <div className='ms-order-confirmation__payment-methods-title'>Payment method</div>
        <div className='ms-order-confirmation__payment-methods-line'>
            <p className='ms-order-confirmation__payment-methods-card'>{getPaymentMethod(salesOrder)}</p>
        </div>
    </div>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<IOrderConfirmationData>> = props => {
    const {
        moduleProps,
          heading,
          backToShoppingLink,
          alert,
          loading,
          payment,
          help,
          data: { orderHydration },
          context: { cultureFormatter: { formatCurrency }},
          salesLines
    } = props;
    const tenderLines = props.data.orderHydration.result?.salesOrder.TenderLines;
    let totalOnAccount = 0;
    if (tenderLines) {
        totalOnAccount = tenderLines.map(tl => tl.AmountInCompanyCurrency || 0).reduce((num, total) => num + total, 0);
    }
    const DLV_MODE_KEY = 'dlv-mode';
    const [dlvMode, setDlvMode] = React.useState<string>('');
    React.useEffect(() => {
        setDlvMode(window.sessionStorage.getItem(DLV_MODE_KEY) || '');
        sessionStorage.removeItem(DLV_MODE_KEY);
    }, []);
    return (
        salesLines?.[0].data.salesLine.IsInvoiceLine ? (
            <Module {...moduleProps}>
                {heading}
                {alert}
                {loading}
                {orderHydration?.result?.salesOrder && (
                <InvoicePaymentConfirmationDetail
                    cultureFormatter={props.context.cultureFormatter}
                    salesOrder={orderHydration?.result?.salesOrder}
                    customer={props.data.accountInformation?.result}
                    deliveryMode={dlvMode}
                    orderedProducts={props.data.orderHydration.result?.products as SimpleProduct[]}
                    resources={props.resources}
                    chargeLinesBreakdown={props.context.app.config.chargeLinesBreakdown}
                    cartLines={[]}
                />)}
                {backToShoppingLink}
                {renderInvoicePaymentMethod(orderHydration?.result?.salesOrder)}
                {help && <OrderConfirmationHelp {...help} />}
            </Module>
            ) : (
            <Module {...moduleProps}>
                {heading}
                {alert}
                {loading}
                {orderHydration?.result?.salesOrder && (
                <OrderConfirmationDetail
                    cultureFormatter={props.context.cultureFormatter}
                    salesOrder={orderHydration?.result?.salesOrder}
                    customer={props.data.accountInformation?.result}
                    deliveryMode={dlvMode}
                    orderedProducts={props.data.orderHydration.result?.products as SimpleProduct[]}
                    resources={props.resources}
                    chargeLinesBreakdown={props.context.app.config.chargeLinesBreakdown}
                />)}
                {backToShoppingLink}
                {payment && <OrderConfirmationPayment
                    {...payment}
                    totalOnAccount={totalOnAccount > 0 ? formatCurrency(totalOnAccount) : undefined}
                />}
                {help && <OrderConfirmationHelp {...help} />}
            </Module>
    ));
};

export default OrderConfirmationView;
